import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  makeStyles,
  Paper,
  TextField,
  Typography,
  Divider,
  InputAdornment,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Info } from "@material-ui/icons";

import { green } from "@material-ui/core/colors";
import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  Edit,
  Visibility,
  Search as SearchIcon,
  ArrowDropDown,
  Refresh,
  ChevronLeft,
  ChevronRight,
} from "@material-ui/icons";

import { PDFViewer } from "../../sasl/batch/updateBatch/StructuredInformation/PDFViewer";
import { appUrl } from "../../../../../../utils/config";

// styling
const useStyles = makeStyles((theme) => ({
  formLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    minWidth: "150px",
    paddingRight: theme.spacing(2),
    textAlign: "right",
  },
  fixedLabel: {
    width: 180,
    textAlign: "left",
    paddingRight: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  requiredStar: {
    color: "red",
    marginLeft: 4,
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  testTable: {
    '& .MuiTableCell-head': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
    },
  },
}));

const PartialManufacturingMedicinalProduct = (props) => {
  const { initialData, onClose, schrockenCustomFileID, digitizedData } = props;
  const [isEditable, setIsEditable] = useState(false);
  const [formValues, setFormValues] = useState(initialData || {});
  const [originalValues, setOriginalValues] = useState(initialData || {});
  const [editedValues, setEditedValues] = useState(initialData || {});
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [modalData, setModalData] = useState({
    field: "",
    before: "",
    after: "",
    updatedBy: "user123",
    timestamp: new Date().toLocaleString(),
  });

  const classes = useStyles();

  useEffect(() => {
    const dataToUse = initialData || {};
    setFormValues(dataToUse);
    setOriginalValues(dataToUse);
    setEditedValues(dataToUse);
  }, [initialData]);

  // Validation functions based on data types
  const validateField = (fieldKey, value) => {
    const validations = {
      "batch_number": {
        type: "Alphanumeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Packaging Batch Number is required";
          if (!/^[A-Za-z0-9]+$/.test(val)) return "Must be alphanumeric";
          return null;
        }
      },
      "bulkBatchNumber": {
        type: "Alphanumeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Bulk Batch Number is required";
          if (!/^[A-Za-z0-9]+$/.test(val)) return "Must be alphanumeric";
          return null;
        }
      },
      "finished_goods_item_number_BMS": {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Finished Goods Item Number (Sponsor) is required";
          if (!/^\d+$/.test(val)) return "Must be numeric";
          return null;
        }
      },
      "finished_goods_item_number_PCI": {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Finished Goods Item Number (PCI) is required";
          if (!/^\d+$/.test(val)) return "Must be numeric";
          return null;
        }
      },
      "expiry_date": {
        type: "Date MM.YYYY",
        validate: (val) => {
          if (!val || val.trim() === '') return "Expiry Date (Finished Pack) is required";
          if (!/^\d{2}\.\d{4}$/.test(val)) return "Must be in MM.YYYY format";
          return null;
        }
      },
      "manufacture_date": {
        type: "Date DD MMM YYYY",
        validate: (val) => {
          if (!val || val.trim() === '') return "Manufacturing Date (As per CoA) is required";
          if (!/^\d{1,2}\s[A-Za-z]{3}\s\d{4}$/.test(val)) return "Must be in DD MMM YYYY format";
          return null;
        }
      },
      "total_weight": {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Total Number of Packs for Shipment is required";
          if (!/^\d+$/.test(val)) return "Must be numeric";
          return null;
        }
      },
      "totalDrugProduct/Britestockconsumed": {
        type: "Numeric",
        validate: (val) => {
          if (!val || val.trim() === '') return "Total Drug Product/Britestock consumed is required";
          if (!/^\d+$/.test(val)) return "Must be numeric";
          return null;
        }
      }
    };

    const fieldValidation = validations[fieldKey];
    if (fieldValidation) {
      return fieldValidation.validate(value);
    }
    return null;
  };

  const validateAllFields = () => {
    const errors = {};
    Object.keys(formValues).forEach(fieldKey => {
      const error = validateField(fieldKey, getValueForField(fieldKey));
      if (error) {
        errors[fieldKey] = error;
      }
    });
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getConfidenceForField = (fieldKey) => {
    const fieldData = formValues[fieldKey];
    if (fieldData && fieldData.confidence) {
      return parseFloat(fieldData.confidence);
    }
    return 90; // Default fallback
  };

  const getValueForField = (fieldKey, dataSource = formValues) => {
    const fieldData = dataSource[fieldKey];
    if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
      return fieldData.value;
    }
    return fieldData || '';
  };

  const getLevelColor = (level) => {
    if (level < 80) return "red";
    if (level <= 90) return "orange";
    return "green";
  };

  const handleInputChange = (fieldKey, value) => {
    const newValues = {
      ...formValues,
      [fieldKey]: {
        ...formValues[fieldKey],
        value: value
      }
    };
    setFormValues(newValues);
    if (!showingOriginal) {
      setEditedValues(newValues);
    }

    // Validate the field and update errors
    const error = validateField(fieldKey, value);
    setValidationErrors(prev => ({
      ...prev,
      [fieldKey]: error
    }));
  };

  const handleSave = async () => {
    if (validateAllFields()) {
      try {
        setSaving(true);
        
        // Parse existing kvpJson and update with current form values
        const existingKvpJson = digitizedData?.kvpJson ? JSON.parse(digitizedData.kvpJson) : {};
        
        // Update kvpJson with current form values
        const updatedKvpJson = {
          ...existingKvpJson,
          "batch_number": {
            value: getValueForField('batch_number'),
            confidence: existingKvpJson["batch_number"]?.confidence || 90
          },
          "bulkBatchNumber": {
            value: getValueForField('bulkBatchNumber'),
            confidence: existingKvpJson["bulkBatchNumber"]?.confidence || 90
          },
          "finished_goods_item_number_BMS": {
            value: getValueForField('finished_goods_item_number_BMS'),
            confidence: existingKvpJson["finished_goods_item_number_BMS"]?.confidence || 90
          },
          "finished_goods_item_number_PCI": {
            value: getValueForField('finished_goods_item_number_PCI'),
            confidence: existingKvpJson["finished_goods_item_number_PCI"]?.confidence || 90
          },
          "expiry_date": {
            value: getValueForField('expiry_date'),
            confidence: existingKvpJson["expiry_date"]?.confidence || 90
          },
          "manufacture_date": {
            value: getValueForField('manufacture_date'),
            confidence: existingKvpJson["manufacture_date"]?.confidence || 90
          },
          "total_weight": {
            value: getValueForField('total_weight'),
            confidence: existingKvpJson["total_weight"]?.confidence || 90
          },
          "totalDrugProduct/Britestockconsumed": {
            value: getValueForField('totalDrugProduct/Britestockconsumed'),
            confidence: existingKvpJson["totalDrugProduct/Britestockconsumed"]?.confidence || 90
          }
        };

        // Prepare API request payload
        const requestPayload = {
          vendorMosymphonyID: digitizedData?.vendorMosymphonyID || 3,
          digitizedExtractedDataList: [{
            poNumber: digitizedData?.poNumber,
            batchNumber: digitizedData?.batchNumber,
            processedFileName: digitizedData?.processedFileName,
            senderEmailID: digitizedData?.senderEmailID,
            emailReceivedTimestamp: digitizedData?.emailReceivedTimestamp,
            attachmentName: digitizedData?.attachmentName,
            documentType: digitizedData?.documentType,
            documentFormat: digitizedData?.documentFormat,
            emailSubject: digitizedData?.emailSubject || "",
            emailBody: digitizedData?.emailBody || "",
            schrockenCustomFileID: digitizedData?.schrockenCustomFileID,
            kvpJson: JSON.stringify(updatedKvpJson),
            siteName: digitizedData?.siteName
          }]
        };

        // Make API call
        const response = await axios.post(
          `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDigitizedExtractedData`,
          requestPayload
        );

        if (response.data.status) {
          // Save changes locally
          setEditedValues(formValues);
          setIsEditable(false);
          setShowingOriginal(false);
          
          //console.log('Partial Manufacturing Medicinal Product data saved successfully');
        } else {
          console.error('Failed to save:', response.data.message);
        }
      } catch (error) {
        console.error('Error saving Partial Manufacturing Medicinal Product data:', error);
      } finally {
        setSaving(false);
      }
    }
  };

  const getFieldErrorProps = (fieldKey) => ({
    error: !!validationErrors[fieldKey],
    helperText: validationErrors[fieldKey]
  });

  const renderField = (fieldKey, label, placeholder, multiline = false, rows = 1) => (
    <Grid
      container
      alignItems="center"
      spacing={2}
      className={classes.formField}
    >
      <Grid item xs={4}>
        <Box display="flex" alignItems="center">
          <Typography
            style={{
              paddingRight: 16,
              fontWeight: 500,
            }}
          >
            {label}
            <span className={classes.requiredStar}>*</span>
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={8}>
        <TextField
          variant="outlined"
          fullWidth
          size="small"
          value={getValueForField(fieldKey)}
          onChange={(e) =>
            handleInputChange(fieldKey, e.target.value)
          }
          onBlur={(e) => {
            const { value } = e.target;
            // Trim leading and trailing spaces for string values
            if (typeof value === "string" && value.trim() !== value) {
              handleInputChange(fieldKey, value.trim());
            }
          }}
          placeholder={placeholder}
          multiline={multiline}
          rows={rows}
          {...getFieldErrorProps(fieldKey)}
          InputProps={{
            readOnly: !isEditable,
            endAdornment: (
              <InputAdornment position="end">
                <Box display="flex" alignItems="center" pl={1}>
                  <Box
                    style={{
                      width: 1,
                      height: multiline ? 50 : 39,
                      backgroundColor: "#ccc",
                      marginRight: 3,
                    }}
                  />
                                     {!isEditable ? (
                     getValueForField(fieldKey) !== getValueForField(fieldKey, originalValues) ? (
                      <Box
                        onClick={() => {
                                                     setModalData({
                             field: label,
                             before: getValueForField(fieldKey, originalValues),
                             after: getValueForField(fieldKey),
                             updatedBy: "user123",
                             timestamp:
                               new Date().toLocaleString(),
                           });
                          setOpenModal(true);
                        }}
                        style={{
                          backgroundColor: "#7777",
                          color: "white",
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          minWidth: 24,
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Info fontSize="small" />
                      </Box>
                    ) : (
                      <Box
                        style={{
                          backgroundColor: getLevelColor(getConfidenceForField(fieldKey)),
                          color: "white",
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          minWidth: 24,
                          textAlign: "center",
                        }}
                      >
                        {Math.round(getConfidenceForField(fieldKey))}
                      </Box>
                    )
                  ) : (
                    <Box
                      style={{
                        backgroundColor: getLevelColor(getConfidenceForField(fieldKey)),
                        color: "white",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        minWidth: 24,
                        textAlign: "center",
                      }}
                    >
                      {Math.round(getConfidenceForField(fieldKey))}
                    </Box>
                  )}
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      <Grid container direction="column" spacing={2}>

        {/* Actions*/}
        <Grid item container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Grid item style={{display: "flex", alignItems: "center"}} spacing={2}>
              <Typography
                variant="h6"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  marginRight: "8px",
                }}
              >
                Partial Manufacturing Data
              </Typography>
              <Box display="flex" gap={1} spacing={2}>
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => {
                    if (isEditable) {
                      handleSave();
                    } else {
                      // Start editing
                      setIsEditable(true);
                      setShowingOriginal(false);
                      setFormValues(editedValues);
                    }
                  }}
                  style={{ marginRight: "8px", minWidth: 15, textAlign: "center" }}
                  disabled={saving}
                >
                  {saving ? "Saving..." : isEditable ? "Save Changes" : "Edit"}
                </Button>
                <Button
                  color="secondary"
                  variant="outlined"
                  onClick={() => {
                    if (showingOriginal) {
                      // Show edited version
                      setFormValues(editedValues);
                      setShowingOriginal(false);
                    } else {
                      // Show original version
                      setFormValues(originalValues);
                      setShowingOriginal(true);
                    }
                    setIsEditable(false);
                  }}
                  style={{ marginRight: "8px", minWidth: 20, textAlign: "center", size: "20px" }}
                >
                  {showingOriginal ? "Show Edited Version" : "Show Unedited Version"}
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">PDF Preview</Typography>
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              {/* Packaging Batch Number */}
              {renderField('batch_number', 'Packaging Batch Number', 'Enter Packaging Batch Number')}

              {/* Bulk Batch Number */}
              {renderField('bulkBatchNumber', 'Bulk Batch Number', 'Enter Bulk Batch Number')}

              {/* Finished Goods Item Number (Sponsor) */}
              {renderField('finished_goods_item_number_BMS', 'Finished Goods Item Number (Sponsor)', 'Enter Finished Goods Item Number (Sponsor)')}

              {/* Finished Goods Item Number (PCI) */}
              {renderField('finished_goods_item_number_PCI', 'Finished Goods Item Number (PCI)', 'Enter Finished Goods Item Number (PCI)')}

              {/* Expiry Date (Finished Pack) */}
              {renderField('expiry_date', 'Expiry Date (Finished Pack)', 'Enter Expiry Date (MM.YYYY)')}

              {/* Manufacturing Date (As per CoA) */}
              {renderField('manufacture_date', 'Manufacturing Date (As per CoA)', 'Enter Manufacturing Date (DD MMM YYYY)')}

              {/* Total Number of Packs for Shipment */}
              {renderField('total_weight', 'Total Number of Packs for Shipment', 'Enter Total Number of Packs for Shipment')}

              {/* Total Drug Product/Britestock consumed */}
              {renderField('totalDrugProduct/Britestockconsumed', 'Total Drug Product/Britestock consumed', 'Enter Total Drug Product/Britestock consumed')}

              <Box display="flex" alignItems="center" gap={2} mb={2} mt={3}>
                <Typography variant="body2" color="textSecondary">
                  Confidence Levels Legend:{" "}
                </Typography>
                <Grid item style={{ width: "28px" }} />
                {/* Green Box */}
                <Box
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 30,
                  }}
                >
                  &gt; 90
                </Box>
                {/* Orange Box */}
                <Box
                  style={{
                    backgroundColor: "orange",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 50,
                    marginLeft: 16,
                  }}
                >
                  80 to 90
                </Box>
                {/* Red Box */}
                <Box
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textAlign: "center",
                    minWidth: 30,
                    marginLeft: 16,
                  }}
                >
                  &lt; 80
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right side: PDF view */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              style={{
                height: "calc(100vh - 200px)",
                padding: 16,
                overflowY: "auto",
              }}
            >
              <Box
                style={{
                  height: 500,
                  backgroundColor: "#f5f5f5",
                  border: "1px dashed #ccc",
                }}
              >
                {schrockenCustomFileID ? (
                  <PDFViewer schrockenCustomFileID={schrockenCustomFileID} />
                ) : (
                  <Box
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography>No PDF available</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            padding: 24,
            boxShadow: 24,
            borderRadius: 8,
            minWidth: 600,
          }}
        >
          <Typography variant="h6" gutterBottom>
            {modalData.field} - Field Data History
          </Typography>

          <Table>
            <TableHead
              style={{ border: "1px solid grey", backgroundColor: "#7777" }}
            >
              <TableRow
                style={{ border: "1px solid grey", backgroundColor: "#7777" }}
              >
                <TableCell>
                  <b>Timestamp</b>
                </TableCell>
                <TableCell>
                  <b>Before Value</b>
                </TableCell>
                <TableCell>
                  <b>After Value</b>
                </TableCell>
                <TableCell>
                  <b>Updated By</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody style={{ border: "1px solid grey" }}>
              <TableRow>
                <TableCell>{modalData.timestamp}</TableCell>
                <TableCell>{modalData.before}</TableCell>
                <TableCell>{modalData.after}</TableCell>
                <TableCell>{modalData.updatedBy}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenModal(false)}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PartialManufacturingMedicinalProduct;
